import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BrainCircuit, History, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { trainingApi } from '../services/api';
import TrainingTabs from '../components/training/TrainingTabs';

/** The programme's training profile: read by viewers, edited by approvers (EP-26 FE-2 ST-9, plan §3.1, §7.1). */
type P = Record<string, any>;
type Change = { path: string; before: unknown; after: unknown };

const clone = (x: unknown) => JSON.parse(JSON.stringify(x ?? {}));

/** Every leaf as path -> value, so two profiles compare field by field. */
function flatten(x: unknown, prefix = '', out: Record<string, unknown> = {}): Record<string, unknown> {
  if (x && typeof x === 'object' && !Array.isArray(x)) {
    const keys = Object.keys(x as P);
    if (!keys.length && prefix) out[prefix] = {};
    keys.forEach((k) => flatten((x as P)[k], prefix ? `${prefix}.${k}` : k, out));
  } else if (Array.isArray(x) && x.some((v) => v && typeof v === 'object')) {
    if (!x.length && prefix) out[prefix] = [];
    x.forEach((v, i) => flatten(v, `${prefix}.${i}`, out));
  } else {
    out[prefix] = x;
  }
  return out;
}

function diff(before: P, after: P): Change[] {
  const a = flatten(before), b = flatten(after);
  return Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort()
    .filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]))
    .map((path) => ({ path, before: a[path], after: b[path] }));
}

/** Marshmallow's nested messages as path -> text. */
function errorPaths(errors: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (x: unknown, prefix: string) => {
    if (Array.isArray(x) && x.every((v) => typeof v === 'string')) out[prefix] = x.join(' ');
    else if (x && typeof x === 'object') Object.entries(x as P).forEach(([k, v]) => walk(v, prefix ? `${prefix}.${k}` : k));
    else if (typeof x === 'string') out[prefix] = x;
  };
  walk(errors, '');
  return out;
}

const show = (v: unknown) => (v === undefined ? '—' : typeof v === 'string' ? v || '""' : JSON.stringify(v));
const get = (o: P, path: string) => path.split('.').reduce((x: any, k) => (x == null ? undefined : x[k]), o);
function set(o: P, path: string, value: unknown): P {
  const next = clone(o); const keys = path.split('.'); let cur = next;
  keys.slice(0, -1).forEach((k) => { if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {}; cur = cur[k]; });
  if (value === undefined) delete cur[keys[keys.length - 1]]; else cur[keys[keys.length - 1]] = value;
  return next;
}

const input = 'px-2 py-1 text-sm border rounded w-full dark:bg-gray-700 dark:border-gray-600 disabled:bg-transparent disabled:border-transparent';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">{title}</h3>
      <div className="grid gap-3 md:grid-cols-2">{children}</div>
    </section>
  );
}

export default function TrainingProfilePage() {
  const { programId = '' } = useParams<{ programId: string }>();
  const [programme, setProgramme] = useState<P | null>(null);
  const [draft, setDraft] = useState<P>({});
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [jsonText, setJsonText] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState<P[]>([]);
  const [proposals, setProposals] = useState<P[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    Promise.all([trainingApi.programme(programId), trainingApi.profileHistory(programId), trainingApi.profileProposals(programId)])
      .then(([p, h, pr]) => { setProgramme(p); setDraft(clone(p.profile)); setHistory(h); setProposals(pr); setMessage(''); })
      .catch((e) => setMessage(String(e?.message || e)))
      .finally(() => setBusy(false));
  }, [programId]);
  useEffect(() => { load(); }, [load]);

  const profile: P = programme?.profile || {};
  const canEdit = !!programme?.can?.approve;
  // An operator (or an agent's token) proposes; approvers decide it in Approvals (FE-2 ST-10).
  const canPropose = !canEdit && !!programme?.can?.operate;
  const canChange = canEdit || canPropose;
  const changes = useMemo(() => diff(profile, draft), [profile, draft]);
  const off = !editing;

  const field = (label: string, path: string, kind: 'text' | 'number' | 'list' = 'text', hint = '') => {
    const v = get(draft, path);
    const shown = kind === 'list' ? (Array.isArray(v) ? v.join(', ') : '') : (v ?? '');
    return (
      <label className="text-xs text-gray-600 dark:text-gray-400 block" key={path}>
        {label}
        <input className={input} disabled={off} value={shown as string | number}
          type={kind === 'number' ? 'number' : 'text'} step="any"
          onChange={(e) => {
            const raw = e.target.value;
            const val = kind === 'list' ? raw.split(',').map((s) => s.trim()).filter(Boolean)
              : kind === 'number' ? (raw === '' ? undefined : Number(raw)) : (raw === '' ? undefined : raw);
            setDraft(set(draft, path, val));
          }} />
        {hint ? <span className="block text-[11px] text-gray-400">{hint}</span> : null}
        {errors[path] ? <span className="block text-[11px] text-red-600">{errors[path]}</span> : null}
      </label>
    );
  };

  const catalogue: P[] = Array.isArray(draft.runCatalogue) ? draft.runCatalogue : [];
  const setEntry = (i: number, key: string, value: unknown) =>
    setDraft(set(draft, 'runCatalogue', catalogue.map((e, j) => (j === i ? { ...e, [key]: value } : e))));
  const stores: [string, string][] = Object.entries(draft.stores || {}) as [string, string][];

  const propose = async () => {
    const reason = window.prompt('Why should the profile change? (approvers see this)');
    if (!reason || !reason.trim()) return;
    setBusy(true); setErrors({}); setMessage('');
    try {
      const out = await trainingApi.proposeProfile(programId, draft, reason.trim());
      setEditing(false); setConfirm(false); setDraft(clone(profile));
      setMessage(`Proposed (${out.changed.join(', ')}). It is now waiting for a training approver in Approvals; approving applies it.`);
      setProposals(await trainingApi.profileProposals(programId));
    } catch (e: any) {
      const errs = errorPaths(e?.detail?.errors);
      setErrors(errs); setConfirm(false);
      setMessage(Object.keys(errs).length ? 'The proposed profile is not valid: see the fields marked below.' : String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true); setErrors({}); setMessage('');
    try {
      const out = await trainingApi.saveProfile(programId, draft);
      setProgramme(out); setDraft(clone(out.profile)); setEditing(false); setConfirm(false);
      setMessage(out.changed ? 'Saved. The change is recorded below.' : 'Nothing changed.');
      setHistory(await trainingApi.profileHistory(programId));
    } catch (e: any) {
      const errs = errorPaths(e?.detail?.errors);
      setErrors(errs); setConfirm(false);
      setMessage(Object.keys(errs).length ? 'The profile is not valid: see the fields marked below.' : String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BrainCircuit className="w-5 h-5" /> Model Training</h1>
        <button type="button" onClick={load} className="ml-auto text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </header>
      <TrainingTabs programId={programId} />
      {!programme && !message && <p className="text-sm text-gray-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Reading the profile…</p>}
      {message && <p className={`text-sm ${Object.keys(errors).length ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>{message}</p>}
      {programme && (<>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            How {programme.name} is judged and run. {canEdit ? 'You can change it (training:approve).' : canPropose ? 'You can propose a change; a training approver decides it.' : 'Read only.'}
          </span>
          {canChange && !editing && (
            <button type="button" onClick={() => { setEditing(true); setErrors({}); setMessage(''); }}
              className="ml-auto px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>)}
          {editing && (<>
            <button type="button" disabled={!changes.length || busy} onClick={() => setConfirm(true)}
              className="ml-auto px-2 py-1 text-xs rounded bg-accent text-white disabled:opacity-50">Review {changes.length} change{changes.length === 1 ? '' : 's'}</button>
            <button type="button" onClick={() => { setEditing(false); setConfirm(false); setDraft(clone(profile)); setErrors({}); setJsonText(null); }}
              className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>
          </>)}
        </div>

        {confirm && (
          <div className="rounded-lg border border-accent p-3 space-y-2">
            <h3 className="text-sm font-semibold">{canEdit ? 'Save these changes?' : 'Propose these changes for approval?'}</h3>
            <table className="w-full text-xs"><tbody>
              {changes.map((c) => (
                <tr key={c.path} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="py-1 pr-2 font-mono">{c.path}</td>
                  <td className="py-1 pr-2 text-red-700 line-through break-all">{show(c.before)}</td>
                  <td className="py-1 text-emerald-700 break-all">{show(c.after)}</td>
                </tr>))}
            </tbody></table>
            <div className="flex gap-2">
              <button type="button" disabled={busy} onClick={canEdit ? save : propose} className="px-2 py-1 text-xs rounded bg-accent text-white disabled:opacity-50">{canEdit ? 'Save' : 'Propose for approval'}</button>
              <button type="button" onClick={() => setConfirm(false)} className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600">Back</button>
            </div>
          </div>)}

        <Section title="Unit">{[field('Name', 'unit.name'), field('Label', 'unit.label')]}</Section>
        <Section title="Pass marks">{[field('Main score minimum', 'passMarks.mainScoreMin', 'number'), field('Main score name', 'passMarks.mainScoreName'),
          field('Unsafe maximum', 'passMarks.unsafeMax', 'number'), field('Minimum evidence', 'passMarks.minEvidence', 'number'),
          field('Abstention recall minimum', 'passMarks.abstentionRecallMin', 'number'), field('Unparseable maximum', 'passMarks.unparseableMax', 'number')]}</Section>
        <Section title="Regression tolerance and depth">{[field('Main score drop allowed', 'regressionTolerance.mainScoreDrop', 'number'),
          field('Unsafe rise allowed', 'regressionTolerance.unsafeRise', 'number'), field('Rows per unit', 'depth.rowsPerUnit', 'number'),
          field('Repetition', 'depth.repetition', 'number')]}</Section>
        <Section title="Process, gates and population">{[field('Process template', 'processTemplate.key'), field('Template version', 'processTemplate.version', 'number'),
          field('Gate provider', 'gateProvider', 'text', 'apto, or empty'), field('Population', 'population'),
          field('Champion', 'champion', 'text', 'Normally set by a promotion'), field('Aggregation', 'aggregation', 'list', 'comma-separated'),
          field('Panels', 'panels', 'list', 'comma-separated')]}</Section>
        <Section title="Compute">{[field('Targets', 'compute.targets', 'list', 'comma-separated; the run catalogue may only name these'),
          field('Monthly budget', 'compute.monthlyBudget', 'number'), field('Currency', 'compute.currency')]}</Section>
        <Section title="Credentials (Vault paths, not secrets)">{[field('Read', 'credentials.read'), field('Write', 'credentials.write')]}</Section>

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Stores</h3>
          <table className="w-full text-xs"><tbody>
            {stores.map(([role, db]) => (
              <tr key={role}><td className="py-1 pr-2 w-40 font-mono">{role}</td>
                <td className="py-1"><input className={input} disabled={off} value={db}
                  onChange={(e) => setDraft(set(draft, `stores.${role}`, e.target.value))} />
                  {errors[`stores.${role}`] ? <span className="text-[11px] text-red-600">{errors[`stores.${role}`]}</span> : null}</td></tr>))}
          </tbody></table>
        </section>

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Run catalogue</h3>
          <p className="text-xs text-gray-500 mb-2">What a runner may execute: each stage once, its entry point in the programme's code, default parameters, and the targets it may run on.</p>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500"><th className="pr-2">Stage</th><th className="pr-2">Entry point</th><th className="pr-2">Targets</th><th className="pr-2">Default params (JSON)</th><th className="pr-2">Description</th><th /></tr></thead>
            <tbody>
              {catalogue.map((e, i) => (
                <tr key={i} className="align-top border-t border-gray-100 dark:border-gray-700">
                  <td className="py-1 pr-2 w-36"><input className={input} disabled={off} value={e.stage || ''} onChange={(ev) => setEntry(i, 'stage', ev.target.value)} /></td>
                  <td className="py-1 pr-2"><input className={input} disabled={off} value={e.entryPoint || ''} onChange={(ev) => setEntry(i, 'entryPoint', ev.target.value)} /></td>
                  <td className="py-1 pr-2 w-40"><input className={input} disabled={off} value={(e.targets || []).join(', ')}
                    onChange={(ev) => setEntry(i, 'targets', ev.target.value.split(',').map((s) => s.trim()).filter(Boolean))} /></td>
                  <td className="py-1 pr-2"><ParamsCell disabled={off} value={e.params || {}} onChange={(v) => setEntry(i, 'params', v)} /></td>
                  <td className="py-1 pr-2"><input className={input} disabled={off} value={e.description || ''} onChange={(ev) => setEntry(i, 'description', ev.target.value)} /></td>
                  <td className="py-1">{editing ? <button type="button" title="Remove" onClick={() => setDraft(set(draft, 'runCatalogue', catalogue.filter((_, j) => j !== i)))}>
                    <Trash2 className="w-3.5 h-3.5 text-red-600" /></button> : null}</td>
                  {Object.entries(errors).filter(([k]) => k.startsWith(`runCatalogue.${i}.`)).map(([k, m]) => (
                    <td key={k} className="text-[11px] text-red-600">{k.split('.').slice(2).join('.')}: {m}</td>))}
                </tr>))}
            </tbody>
          </table>
          {errors.runCatalogue ? <p className="text-[11px] text-red-600">{errors.runCatalogue}</p> : null}
          {editing && (
            <button type="button" onClick={() => setDraft(set(draft, 'runCatalogue', [...catalogue, { stage: '', entryPoint: '', targets: [], params: {}, description: '' }]))}
              className="mt-2 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add a stage</button>)}
        </section>

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">The whole profile (JSON)</h3>
          <p className="text-xs text-gray-500 mb-2">Everything above, plus any field the form does not show (for example streams). Edits here replace the draft when applied.</p>
          <textarea className={`${input} font-mono h-48`} disabled={off} value={jsonText ?? JSON.stringify(draft, null, 2)}
            onChange={(e) => setJsonText(e.target.value)} />
          {editing && jsonText !== null && (
            <button type="button" className="mt-1 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600"
              onClick={() => { try { setDraft(JSON.parse(jsonText)); setJsonText(null); setMessage(''); } catch { setMessage('That JSON does not parse.'); } }}>
              Apply the JSON to the draft</button>)}
        </section>

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Proposed changes</h3>
          {!proposals.length && <p className="text-xs text-gray-500 mb-2">No proposals.</p>}
          {proposals.map((pr) => (
            <details key={pr.taskId} className="text-xs border-t border-gray-100 dark:border-gray-700 py-1">
              <summary className="cursor-pointer">
                {String(pr.proposedAt || '').replace('T', ' ').slice(0, 16)} · {pr.proposedBy} · {(pr.changed || []).join(', ')} ·{' '}
                {pr.applied ? <span className="text-emerald-600">applied (approved by {(pr.applied.approvedBy || []).join(', ')})</span>
                  : pr.stale ? <span className="text-amber-700">stale: {pr.stale}</span>
                  : pr.status === 'rejected' ? <span className="text-red-600">rejected</span>
                  : <span className="text-sky-700">waiting for approval{pr.pendingApproval ? ` (${pr.pendingApproval.approvals}/${pr.pendingApproval.required})` : ''} ·{' '}
                      <a href="/approvals" className="underline">decide in Approvals</a></span>}
              </summary>
              <p className="mt-1 text-gray-600 dark:text-gray-400">Why: {pr.reason}</p>
              <table className="w-full mt-1"><tbody>
                {diff(pr.before || {}, pr.proposed || {}).map((c) => (
                  <tr key={c.path}><td className="pr-2 font-mono">{c.path}</td><td className="pr-2 text-red-700 line-through break-all">{show(c.before)}</td>
                    <td className="text-emerald-700 break-all">{show(c.after)}</td></tr>))}
              </tbody></table>
            </details>))}
          <h3 className="mt-3 text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1"><History className="w-4 h-4" /> Changes</h3>
          {!history.length && <p className="text-xs text-gray-500">No recorded changes.</p>}
          {history.map((h, i) => (
            <details key={i} className="text-xs border-t border-gray-100 dark:border-gray-700 py-1">
              <summary className="cursor-pointer">{String(h.at || '').replace('T', ' ').slice(0, 16)} · {h.by || 'unknown'} · {diff(h.before || {}, h.after || {}).length} field(s)</summary>
              <table className="w-full mt-1"><tbody>
                {diff(h.before || {}, h.after || {}).map((c) => (
                  <tr key={c.path}><td className="pr-2 font-mono">{c.path}</td><td className="pr-2 text-red-700 line-through break-all">{show(c.before)}</td>
                    <td className="text-emerald-700 break-all">{show(c.after)}</td></tr>))}
              </tbody></table>
            </details>))}
        </section>
      </>)}
    </div>
  );
}

function ParamsCell({ value, disabled, onChange }: { value: P; disabled: boolean; onChange: (v: P) => void }) {
  const [text, setText] = useState(JSON.stringify(value));
  const [bad, setBad] = useState(false);
  useEffect(() => { setText(JSON.stringify(value)); setBad(false); }, [value]);
  return (
    <>
      <input className={`${input} font-mono`} disabled={disabled} value={text}
        onChange={(e) => {
          setText(e.target.value);
          try { const v = JSON.parse(e.target.value || '{}'); if (v && typeof v === 'object' && !Array.isArray(v)) { setBad(false); onChange(v); } else setBad(true); } catch { setBad(true); }
        }} />
      {bad ? <span className="text-[11px] text-red-600">Not a JSON object</span> : null}
    </>
  );
}
